import { EventEmitter, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { FieldValue, Ownership, QuickReport } from '../model';
import { IncomeStatementType, PageNumbers } from '../model/Enums';
import { Report } from '../model/report';
import { ReportProvider } from '../providers';
import { startCase } from 'lodash';
import { CurrencyPipe } from '../pipes/currency.pipe';
import * as moment from 'moment';
import * as _ from 'lodash';
import { DatePipe } from '@angular/common';
import {BsModalRef} from 'ngx-bootstrap/modal';
import * as semver from 'semver';

@Injectable()
export class ReportService {

  private reportId = '';
  private static TAXES_ADJUSTMENT: number = 0.11;

  public report = new BehaviorSubject<Report>(null);
  public report$ = this.report.asObservable();

  public quickReport = new BehaviorSubject<QuickReport>(null);
  public quickReport$ = this.quickReport.asObservable();
  public isQuickReport: boolean = false;

  refreshIncomeStatementRevenue: EventEmitter<boolean> = new EventEmitter();
  incomeShowInterimWarning: EventEmitter<string> = new EventEmitter();
  updateConclusionPage: EventEmitter<boolean> = new EventEmitter();

  // for pending pages
  invalidStages: Array<number> = [];

  constructor(private reportProvider: ReportProvider,
    public currencyPipe: CurrencyPipe,
    private datePipe: DatePipe) { }

  public setReport(newReport: Report): void {
    this.report.next(newReport);
  }

  public clearReport(): void {
    this.invalidStages = [];
    this.isQuickReport = false;
    this.report.next(null);
  }

  public setQuickReport(qReport: QuickReport): void {
    this.isQuickReport = true;
    this.quickReport.next(qReport);
  }

  public clearQuickReport(): void {
    this.invalidStages = [];
    this.isQuickReport = false;
    this.quickReport.next(null);
  }

  public showRentAdjustments(): boolean {
    if (this.report.getValue() && this.report.getValue().LATEST && this.report.getValue().LATEST.LOCATION_LIST)
      for (let i = 0; i < this.report.getValue().LATEST.LOCATION_LIST.length; i++)
        if (this.report.getValue().LATEST.LOCATION_LIST[i].IS_OWNED === true ||
          (this.report.getValue().LATEST.LOCATION_LIST[i].IS_OWNED === false && this.report.getValue().LATEST.LOCATION_LIST[i].IS_RENT_INCREASE === true))
          return true;
    return false;
  }

  checkStringWithTrim(value: string): boolean {
    if (value && value.trim().length > 0)
      return true;
    else
      return false;
  }

  getTotal(values: any[]): number {
    return values.reduce((summ, v) => (summ += parseFloat(v) || 0), 0);
  }

  ownersCombinedWork(): number {
    let total = 0;
    if (this.report.getValue().LATEST.OWNERSHIP_LIST) {
      for (let i = 0; i < this.report.getValue().LATEST.OWNERSHIP_LIST.length; i++) {
        if (Number(this.report.getValue().LATEST.OWNERSHIP_LIST[i].HOURS))
          total += Number(this.report.getValue().LATEST.OWNERSHIP_LIST[i].HOURS);
      }
    }
    return total;
  }

  hasOwnershipAdjustments(): boolean {
    if (this.report.getValue()?.LATEST?.OWNERSHIP_LIST) {
      if (this.report.getValue().LATEST.OWNERSHIP_LIST.length === 1) {
        if (Number.isInteger(Number(this.report.getValue().LATEST.OWNERSHIP_LIST[0].HOURS)))
          if (Number(this.report.getValue().LATEST.OWNERSHIP_LIST[0].HOURS) >= 16)
            return true;
      } else {
        let combinedWork = this.ownersCombinedWork();
        if (Number.isInteger(combinedWork))
          if (combinedWork >= 16)
            return true;
      }
    }
    return false;
  }

  public getRSOwnerHi(): Ownership {
    if (this.hasOwnershipAdjustments()) {
      this.report.getValue().LATEST.OWNERSHIP_LIST.sort((a, b) => Number(a.ORDER) - Number(b.ORDER));
      let ownersAdj: number = -1;
      let ownerIndex: number = -1;

      if (this.report.getValue()?.LATEST?.OWNERSHIP_LIST?.length === 1)
        return this.report.getValue().LATEST.OWNERSHIP_LIST[0];

      for (let i = 0; i < this.report.getValue()?.LATEST?.OWNERSHIP_LIST?.length; i++) {
        const repSal = this.report.getValue().LATEST.OWNERSHIP_LIST[i].REPLACEMENT_SALARY ? this.report.getValue().LATEST.OWNERSHIP_LIST[i].REPLACEMENT_SALARY.toString() : '0';
        if (Number(this.currencyPipe.parse(repSal)) > ownersAdj && this.report.getValue().LATEST.OWNERSHIP_LIST[i].HOURS > 0) {
          ownersAdj = Number(this.currencyPipe.parse(repSal));
          ownerIndex = i;
        }
      }

      if (ownerIndex >= 0)
        return this.report.getValue().LATEST.OWNERSHIP_LIST[ownerIndex];
      else return null;
    }
    return null;
  }

  public getRSOwnerLow(): Array<Ownership> {
    if (this.hasOwnershipAdjustments()) {
      this.report.getValue().LATEST.OWNERSHIP_LIST.sort((a, b) => Number(a.ORDER) - Number(b.ORDER));
      let ownerArray: Array<Ownership> = [];

      if (this.getRSOwnerHi() != null) {
        let hiOwner = this.getRSOwnerHi();

        for (let i = 0; i < this.report.getValue().LATEST.OWNERSHIP_LIST.length; i++)
          if (hiOwner?.ID != this.report.getValue().LATEST.OWNERSHIP_LIST[i].ID && this.report.getValue().LATEST.OWNERSHIP_LIST[i].HOURS > 0)
            ownerArray.push(this.report.getValue().LATEST.OWNERSHIP_LIST[i]);
        return ownerArray;
      }
    }
    return [];
  }

  hasOwnershipUnnamedGmAdjustments(): boolean {
    if (this.report.getValue()?.LATEST?.OWNERSHIP_LIST?.length > 0) {
      if (this.report.getValue().LATEST.OWNERSHIP_LIST.length === 1) {
        if (Number.isInteger(Number(this.report.getValue().LATEST.OWNERSHIP_LIST[0].HOURS)))
          if (Number(this.report.getValue().LATEST.OWNERSHIP_LIST[0].HOURS) <= 15)
            return true;
      } else {
        let combinedWork = this.ownersCombinedWork();
        if (Number.isInteger(combinedWork))
          if (combinedWork <= 15)
            return true;
      }
    } else return true;
    return false;
  }

  showLocationAmountPage(): boolean {
    return this.report.getValue() && this.report.getValue().LATEST
      && this.report.getValue().LATEST.LOCATION_LIST.length ? false : true;
  }

  showOwnerAmountPage(): boolean {
    return this.report.getValue() && this.report.getValue().LATEST
      && this.report.getValue().LATEST.OWNERSHIP_LIST.length ? false : true;
  }

  public calculateTaxAdjustment(value: number = 0): number {
    value = value || 0;
    value = Number(value.toString().replace(/[^\w\s]/gi, '')) | 0;
    return Math.ceil(value * ReportService.TAXES_ADJUSTMENT);
  }

  /**
   *
   * @returns 0 = No Ownership Salary
   * @returns 1 = hrs/week < 16 - General Manager Salary
   * @returns 2 = hrs/week >= 16 - Fair Market Replacement Salary
   */
  showOwnershipSalary(): number {
    if (this.report.getValue() && this.report.getValue().LATEST && this.report.getValue().LATEST.OWNERSHIP_LIST && this.report.getValue().LATEST.OWNERSHIP_LIST.length) {
      const ownerList = this.report.getValue().LATEST.OWNERSHIP_LIST;
      const totalHours = ownerList.reduce((summ, v) => (summ += Number(v.HOURS) || 0), 0);
      if (totalHours >= 0 && totalHours < 16) {
        return 1;
      } else if (totalHours >= 16) {
        return 2;
      } else {
        return 0;
      }
    }
    return 0;
  }

  /**
   *
   * @returns true: show Weighting
   * @returns false: show Projections
   */
  showWeighting(): boolean {
    return this.report.getValue() && this.report.getValue().LATEST
      && !this.report.getValue().LATEST.COVID_AFFECTED
      && this.report.getValue().LATEST.INTERIM_FUTURE_INDICATION;
  }

  public hasOwnedLocationsOnBalance(): boolean {
    if (this.report?.getValue()?.LATEST?.LOCATION_LIST?.length)
      return this.report.getValue().LATEST.LOCATION_LIST.some(el => el.IS_OWNED && el.IS_OWNED_ON_BS);
    return false;
  }

  updateMaxStage(pageNumber: PageNumbers, isForcefully: boolean = false) {
    if (this.report?.getValue()?.LATEST?.MAX_STAGE < pageNumber || isForcefully) {
      this.report.getValue().LATEST.MAX_STAGE = pageNumber;
      let objFieldValue = new FieldValue();
      objFieldValue.GROUP = 'REPORT_VERSION';
      objFieldValue.FIELD = 'MAX_STAGE';
      objFieldValue.VALUE = pageNumber.toString();
      this.reportProvider.updateReportField(this.report.getValue().LATEST.ID, objFieldValue).subscribe();
    }
  }

  addInvalidStage(stage: number, isValid: boolean, isSkippable: boolean) {
    // '----------------------------------------'
    // invalidStages print
    // '----------------------------------------'
    const stageIndex = this.invalidStages.indexOf(stage);
    if (stageIndex >= 0) {
      if (isValid) {
        this.invalidStages.splice(stageIndex, 1);
      }
    } else {
      if (!isSkippable) {
        if (!isValid) {
          this.invalidStages.push(stage);
        }
      }
    }
  }

  /**
   *
   */
  spliceStage() {
    if (!this.showRentAdjustments()) {
      const stageIndex = this.invalidStages.indexOf(PageNumbers.RENT_DETAILS);
      if (stageIndex >= 0) {
        this.invalidStages.splice(stageIndex, 1);
      }
    } else if (this.showOwnershipSalary()) {
      if (this.showOwnershipSalary() == 1) {
        const stageIndex = this.invalidStages.indexOf(PageNumbers.REPLACEMENT_SALARY);
        if (stageIndex >= 0) {
          this.invalidStages.splice(stageIndex, 1);
        }
      } else if (this.showOwnershipSalary() == 2) {
        const stageIndex1 = this.invalidStages.indexOf(PageNumbers.MANAGER_SALARY);
        if (stageIndex1 >= 0) {
          this.invalidStages.splice(stageIndex1, 1);
        }
      }
    }
  }

  /** Round the given value based on precision */
  round(value, precision): number {
    let multiplier = Math.pow(10, precision || 0);
    return Math.round(value * multiplier) / multiplier;
  }

  getStageNumber(routeUrl: string): number {
    const urlWithoutQueryParam = routeUrl.split('?')[0];
    let stageNo = null;
    switch (urlWithoutQueryParam) {
      // comment for alternative flow
      //
      /* case 'entity-reference':
        return PageNumbers.ENTITY_REFERENCE;
        break;
      case 'industry-name':
        return PageNumbers.INDUSTRY_NAME;
        break;
      case 'seller-business':
        return PageNumbers.SELLER_BUSINESS;
        break;
      case 'company-dba':
        return PageNumbers.COMPANY_DBA;
        break;
      case 'business-entity':
        return PageNumbers.BUSINESS_ENTITY;
        break;
      case 'franchise':
        return PageNumbers.FRANCHISE;
        break;
      case 'transaction-type':
        return PageNumbers.TRANSACTION_TYPE;
        break; */
      case 'overview':
        stageNo = PageNumbers.OVERVIEW;
        break;
      /* case 'covid-impact':
        return PageNumbers.COVID_IMPACT;
        break;
      case 'service-type':
        return PageNumbers.SERVICE_TYPE;
        break;
      case 'closures-restrictions':
        return PageNumbers.CLOSURES_RESTRICTIONS;
        break;
      case 'additional-debt':
        return PageNumbers.ADDITIONAL_DEBT;
        break; */
      case 'special-consideration':
        stageNo = PageNumbers.SPECIAL_CONSIDERATION;
        break;
      /* case 'location-amount':
        return PageNumbers.LOCATION_AMOUNT;
        break;
      case 'location-details':
        return PageNumbers.LOCATION_DETAILS;
        break; */
      case 'location':
        stageNo = PageNumbers.LOCATION;
        break;
      /* case 'owner-amount':
        return PageNumbers.OWNER_AMOUNT;
        break;
      case 'owner-details':
        return PageNumbers.OWNER_DETAILS;
        break; */
      /* case 'ownership':
        return PageNumbers.OWNERSHIP;
        break;
      case 'year-end':
        return PageNumbers.YEAR_END;
        break;
      case 'recent-financials':
        return PageNumbers.RECENT_FINANCIALS;
        break;
      case 'income-table':
        return PageNumbers.INCOME_TABLE;
        break;
      case 'future-year':
        return PageNumbers.FUTURE_YEAR;
        break; */
      case 'income':
        stageNo = PageNumbers.INCOME;
        break;
      case 'future-projection':
        stageNo = PageNumbers.FUTURE_PROJECTIONS;
        break;
      case 'rent-details':
        stageNo = PageNumbers.RENT_DETAILS;
        break;
      case 'manager-salary':
        stageNo = PageNumbers.MANAGER_SALARY;
        break;
      case 'replacement-salary':
        stageNo = PageNumbers.REPLACEMENT_SALARY;
        break;
      case 'adjustments':
        stageNo = PageNumbers.ADJUSTMENTS;
        break;
      // case 'future-prediction':
      //   return PageNumbers.FUTURE_PREDICTION;
      //   break;
      case 'weighting-table':
        stageNo = PageNumbers.WEIGHTING_TABLE;
        break;
      case 'projection-table':
        stageNo = PageNumbers.PROJECTION_TABLE;
        break;
      /* case 'purchase-price':
        return PageNumbers.PURCHASE_PRICE;
        break;
      case 'assets':
        return PageNumbers.ASSETS;
        break;
      case 'liabilities':
        return PageNumbers.LIABILITIES;
        break; */
      case 'assets-liabilities':
        stageNo = PageNumbers.ASSETS_LIABILITIES;
        break;

      /* case 'customers-amount':
        return PageNumbers.CUSTOMERS_AMOUNT;
        break;
      case 'div15-annual-revenue':
        return PageNumbers.DIV15_ANNUAL_REVENUE;
        break;
      case 'div50-annual-revenue':
        return PageNumbers.DIV50_ANNUAL_REVENUE;
        break;
      case 'notable-supplier':
        return PageNumbers.NOTABLE_SUPPLIER;
        break; */
      case 'diversification':
        stageNo = PageNumbers.DIVERSIFICATION;
        break;
      case 'personal-goodwill':
        stageNo = PageNumbers.PERSONAL_GOODWILL;
        break;
      case 'barriers-entry':
        stageNo = PageNumbers.BARRIERS_ENTRY;
        break;
      case 'recurring-revenue':
        stageNo = PageNumbers.RECURRING_REVENUE;
        break;
      case 'conclusion':
        stageNo = PageNumbers.CONCLUSION;
        break;
      default:
        stageNo = null;
        break;
    }
    return stageNo;
  }

  /**
   * @description remove _ and make first char capital
   * @param text
   * @returns
   */
  changeTextStyle(text: string) {
    if (text)
      return startCase(text.toLowerCase());
    else
      return null;
  }

  /**
   * for check field names of IST
   * @param fieldName
   */
  checkIncomeStatememtFields(fieldName: string): boolean {
    let listOfFields = ['REVENUE', 'PROFIT', 'INTEREST', 'DEPRECIATION', 'AMORTIZATION', 'OFFICER_COMP', 'GUARANTEED_PAYMENTS'];
    return listOfFields.some(lf => lf === fieldName);
  }

  wsMessageReceived(message: any): void {
    if (message && _.isArray(message)) {
      for (let i = 0; i < message.length; i++) {
        const element = message[i];
        if (element.GROUP === 'INCOME_STATEMENT') {
          const incomeStatementIndex = this.report.getValue().LATEST.INCOME_STATEMENT.findIndex(x => x.ID === element.ID);
          if (incomeStatementIndex != -1 && element.FIELD != 'ACTION_VALUE') {
            // for Adjustment page
            this.report.getValue().LATEST.INCOME_STATEMENT[incomeStatementIndex][element.FIELD] = element.VALUE;
          } else {
            // this is for income statement actions and most recent fin date change
            if (element.FIELD === 'ACTION_VALUE' && element.VALUE) {
              this.report.getValue().LATEST.INCOME_STATEMENT[incomeStatementIndex][element.VALUE.field] = element.VALUE.value;
              this.report.getValue().LATEST.INCOME_STATEMENT_FUNCTIONS[element.VALUE.field] = element.VALUE.function;
              // this is for validate interim revenue field
              if (element.VALUE.field == 'REVENUE') {
                this.refreshIncomeStatementRevenue.emit();
              }
              if (this.report.getValue().LATEST.ASSISTED_INTERIM_CALCULATION && this.checkIncomeStatememtFields(element.VALUE.field)) {
                this.incomeShowInterimWarning.emit(element.VALUE.field);
              }
            }
          }
        } else if (element.GROUP === 'WEIGHTING') {
          if (element.ID === this.report.getValue().LATEST.ID) {
            switch (element.FIELD) {
              // for Weighting page
              case 'WEIGHTED_REVENUE':
                this.report.getValue().LATEST.WEIGHTED_REVENUE = element.VALUE;
                break;
              case 'WEIGHTED_SDE':
                this.report.getValue().LATEST.WEIGHTED_SDE = element.VALUE;
                break;
              case 'WEIGHTED_EBITDA':
                this.report.getValue().LATEST.WEIGHTED_EBITDA = element.VALUE;
                break;
            }
          }
        } else if (element.GROUP === 'PROJECTION') {
          const projectionIndex = this.report.getValue().LATEST.PROJECTION_LIST.findIndex(x => x.ID === element.ID);
          if (projectionIndex != -1) {
            this.report.getValue().LATEST.PROJECTION_LIST[projectionIndex][element.FIELD] = element.VALUE;
          }
        } else if (element.GROUP === 'REPORT_VERSION') {
          if (element.ID == this.report.getValue().LATEST.ID) {
            if (element.FIELD == 'COMPLETION') {
              this.report.getValue().LATEST.COMPLETION = element.VALUE;
            } else if (element.FIELD == 'FYE_DATE') {
              this.report.getValue().LATEST.FYE_DATE = element.VALUE;
            }
          }
        } else if (element.GROUP === 'LOCATION') {
          const locationIndex = this.report.getValue().LATEST.LOCATION_LIST.findIndex(x => x.ID === element.ID);
          if (locationIndex != -1) {
            this.report.getValue().LATEST.LOCATION_LIST[locationIndex].MARKET_RENT = element.VALUE || null;
          }
        }
      }
    } else if (message && !_.isArray(message)) {
      const element = message;
      if (element.GROUP === 'LOCATION') {
        const locationIndex = this.report.getValue().LATEST.LOCATION_LIST.findIndex(x => x.ID === element.ID);
        if (element.FIELD === 'RENT_ADJUSTMENT_YEARLY') {
          if (locationIndex != -1) {
            this.report.getValue().LATEST.LOCATION_LIST[locationIndex].RENT_ADJUSTMENT_YEARLY = element.VALUE;
          }
        }

      }
    }
  }

  getUpdatedStatusOfReport() {
    this.reportProvider.getBillingStatus(this.report.getValue().ID).subscribe(resp => {
      if (resp.success) {
        const result = resp.result;
        this.report.getValue().STATE = result.STATE;
        this.report.getValue().UNLOCKED_EXP = result.UNLOCKED_EXP;
        this.report.getValue().UNLOCKED_AT = result.UNLOCKED_AT;
        this.report.getValue().BASE_PRICE = result.PRICE;
      }
    }, err => {
      // Error Handling
    });
  }

  getReportExpireRemainingDay(): number {
    const todayDate = moment(new Date());
    const expDate = moment(new Date(this.report?.getValue()?.UNLOCKED_EXP));
    return expDate.diff(todayDate, 'days');
  }

  getFYEDate(formate: string = 'yyyy-M-d', forDisply: boolean = false): string {
    if (this.report?.getValue()?.LATEST?.FYE_DATE) {
      let dateFormate = new Date(this.report.getValue().LATEST.FYE_DATE);
      if (forDisply) {
        dateFormate.setDate(dateFormate.getDate());
      } else {
        dateFormate.setDate(dateFormate.getDate() - 1);
      }
      return this.datePipe.transform(dateFormate, formate);
    }
  }

  getForecastedDate(formate: string = 'yyyy-M-d'): string {
    if (this.report?.getValue()?.LATEST?.INCOME_STATEMENT.some(is => is.IS_TYPE === IncomeStatementType.FORECASTED)) {
      const forcastedColumn = this.report.getValue().LATEST.INCOME_STATEMENT.find(is => is.IS_TYPE === IncomeStatementType.FORECASTED);
      let forecastedDate = new Date(forcastedColumn['FYE_DATE']);
      return this.datePipe.transform(forecastedDate, formate);
    }
  }

  makeElementFocus(fieldId: string) {
    document.getElementById(fieldId).focus();
    setTimeout(() => {
      document.activeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center'
      });
    }, 20);
  }

  outdatedVersion(): boolean {
    if (semver.lt(this.report.getValue().LATEST.ALG_VERSION, this.report.getValue().CURRENT_ALG_VERSION)) {
      return true;
    }
    return false;
  }

}
